import React, {
  Dispatch,
  FC,
  Reducer,
  SetStateAction,
  useEffect,
  useReducer,
} from 'react';

import axios from 'axios';

import IndexTable from './IndexTable';
import TopBar from './TopBar';
import Filters from './Filters';
import { DropdownOption } from '../../common/components/SearchFilters/FilterDropdown';
import TablePagination from '../../common/components/TablePagination';
import { pushQueryParam } from '../../common/utils/history';
import LoadingOverlay from '../../common/components/LoadingOverlay';
import Tabs from './Tabs';

interface User {
  id: number;
  address: string;
  email: string;
  fullName: string;
  dateOfBirth: string;
  lastPositiveAt: string;
  phoneFormatted: string;
  phoneNumber: string;
  state: string;
  age: number;
}

interface AssignedUser {
  id: number;
  fullName: string;
}

interface LastAttempt {
  adminId: number;
  adminFullName: string;
  date: string;
  time: string;
}

interface TestConfiguration {
  id: number;
  displayName: string;
  serviceSpecification: string;
}

interface TestStrip {
  barcode: string;
}

export interface Test {
  id: number;
  uid: string;
  caseManagerResult: JSON;
  caseReportStatus: string;
  localizedResultsAtDate: string;
  localizedResultsAtTime: string;
  user: User;
  assignedUser?: AssignedUser;
  attemptsCount: number;
  caseReportPath: string;
  lastAttempt?: LastAttempt;
  timeZone: string;
  testConfigurationId: number;
  testConfiguration?: TestConfiguration;
  testStrip?: TestStrip;
  testGroup: TestGroup;
  caseManagerResults: TestResult[];
  resultSchema?: TestCaseManagerResultSchema;
  results: TestResult[];
  notes: string;
  editPath: string;
  surveyPath: string;
  versionsPath: string;
}

interface TestGroup {
  id: number;
  name: string;
  slug: string;
}

interface TestResult {
  result: any;
  label: string;
  value: string;
  labelledValue?: string;
  testConfigurationId: number;
  localizedResultsAtDate: string;
  localizedResultsAtTime: string;
}

interface TestCaseManagerResultSchema {
  caseManagerResults: TestCaseManagerResultSchemaItem[];
}

interface TestCaseManagerResultSchemaItem {
  label: string;
  value_type: string;
}

export interface CaseReportStatus {
  value: string;
  label: string;
  isResolved: boolean;
}

export interface Appointment {
  id: number;
  uids: string[];
  localizedStartsAtDate: string;
  localizedStartsAtTime: string;
  caseReportStatuses: CaseReportStatus[];
  user: User;
  assignedUsers?: AssignedUser[];
  attemptsCounts: number[];
  caseReportPath: string;
  lastAttempts?: LastAttempt[];
  timeZone: string;
  testConfigurations?: TestConfiguration[];
  testGroup: TestGroup;
  abnormalResultsCount: number;
  positiveResultsCount: number;
  pendingResultsCount: number;
  resultsAtList?: string[];
  resultSchema?: TestCaseManagerResultSchema;
  tests?: Test[];
  accessCode: string;
}

export interface AssignableUser {
  id: number;
  fullName: string;
}

export interface FilterOptions {
  assignees: DropdownOption[];
  organizations: DropdownOption[];
  statuses: DropdownOption[];
  attempts: DropdownOption[];
  testConfigurations: DropdownOption[];
  caseManagerResults: DropdownOption[];
  requisitioningPhysicians: DropdownOption[];
  timeZones: DropdownOption[];
}

interface Meta {
  currentPage: number;
  nextPage?: number;
  prevPage?: number;
  totalPages: number;
  totalCount: number;
  perPage: number;
}

interface State {
  appointmentsCount?: number;
  appointments: Appointment[];
  selectedAppointments: Appointment[];
  meta?: Meta;
  currentPage: number;
  loading: boolean;
  caseReportType: string;
  errored: boolean;
}

interface SetAppointmentsCountAction {
  type: 'SET_APPOINTMENTS_COUNT';
  count: number;
}

interface SetDataAction {
  type: 'SET_DATA';
  payload: {
    appointments: Appointment[];
    meta?: Meta;
  };
}

interface SetSelectedAppointmentsAction {
  type: 'SET_SELECTED_APPOINTMENTS';
  appointmentIds: number[];
}

interface SetPageAction {
  type: 'SET_PAGE';
  page: number;
}

interface StartLoadAction {
  type: 'SET_LOADING';
}

interface CaseReportTypeAction {
  type: 'SET_CASE_REPORT_TYPE';
  caseReportType: string;
}

interface ErrorAction {
  type: 'ERROR';
}

type Action =
  | SetAppointmentsCountAction
  | SetDataAction
  | SetSelectedAppointmentsAction
  | SetPageAction
  | StartLoadAction
  | CaseReportTypeAction
  | ErrorAction;

const reducer = (state: State, action: any): State => {
  switch (action.type) {
    case 'SET_APPOINTMENTS_COUNT':
      return {
        ...state,
        appointmentsCount: action.count,
      };
    case 'SET_DATA':
      return {
        ...state,
        ...action.payload,
        loading: false,
      };
    case 'SET_SELECTED_APPOINTMENTS':
      return {
        ...state,
        selectedAppointments: state.appointments.filter((appointment) =>
          action.appointmentIds.includes(appointment.id),
        ),
      };
    case 'SET_PAGE':
      pushQueryParam('page', action.page, { turbolinks: {} });

      return {
        ...state,
        currentPage: action.page,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: true,
      };
    case 'SET_CASE_REPORT_TYPE':
      return {
        ...state,
        caseReportType: action.caseReportType,
      };
    case 'ERROR':
      return {
        ...state,
        loading: false,
        errored: true,
      };
    default:
      return state;
  }
};

const globalCaseManagerPath = ({
  countOnly = false,
  filteredUserId = null,
} = {}) => {
  const url = new URL(window.location.href);
  if (countOnly) {
    url.searchParams.append('count_only', 'true');
  }

  if (filteredUserId) {
    url.searchParams.append('filtered_user_id', filteredUserId.toString());
  }

  url.pathname = '/global_case_manager.json';
  return url.toString();
};

const fetchAppointmentsCount = async ({ filteredUserId }) =>
  axios
    .get(
      globalCaseManagerPath({
        countOnly: true,
        filteredUserId: filteredUserId,
      }),
    )
    .then((response) => response.data);

const fetchAppointments = async ({ filteredUserId }) => {
  const response = await axios.get(globalCaseManagerPath({ filteredUserId }));
  return response.data;
};

export const fetchAppointmentTests = async (
  id: number,
  testGroup: TestGroup,
): Promise<Test[]> => {
  try {
    const response = await axios.get(
      `/test_groups/${testGroup.id}/appointments/${id}/tests`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching appointment tests:', error);
    throw error;
  }
};

export const assignTest = (
  appointment: Appointment,
  test: Test,
  userId: number,
  setTests?: Dispatch<SetStateAction<Test[]>>,
) => {
  axios
    .put(
      `/test_groups/${appointment.testGroup.slug}/appointment_case_reports/${appointment.id}.json`,
      {
        assigned_user_id: userId,
        test_id: test.id,
      },
    )
    .then(async () => {
      setTests &&
        setTests(
          await fetchAppointmentTests(appointment.id, appointment.testGroup),
        );
    });
};

export const unassignTest = (
  appointment: Appointment,
  test: Test,
  setTests?: Dispatch<SetStateAction<Test[]>>,
) => {
  axios
    .put(
      `/test_groups/${appointment.testGroup.slug}/appointment_case_reports/${appointment.id}.json`,
      {
        assigned_user_id: null,
        test_id: test.id,
      },
    )
    .then(async () => {
      setTests &&
        setTests(
          await fetchAppointmentTests(appointment.id, appointment.testGroup),
        );
    });
};

interface IndexProps {
  assignableUsers: AssignableUser[];
  filterOptions: FilterOptions;
  skipPagination?: boolean;
  filteredUserId?: number;
  hasCaseReportTypeTabs?: boolean;
  skipInitialLoad?: boolean;
}

const Index: FC<IndexProps> = ({
  assignableUsers,
  filterOptions,
  skipPagination = false,
  filteredUserId = null,
  hasCaseReportTypeTabs = true,
  skipInitialLoad = !new URLSearchParams(window.location.search).size, // Prevent initial load of tests if no query params are present
}) => {
  const params = new URLSearchParams(window.location.search);
  const initialState: State = {
    appointments: [],
    selectedAppointments: [],
    currentPage: 0,
    loading: false,
    caseReportType: params.get('case_report_type') || 'priority',
    errored: false,
  };

  const [state, dispatch] = useReducer<Reducer<State, Action>>(
    reducer,
    initialState,
  );

  const setSelectedAppointments = (appointmentIds: number[]) =>
    dispatch({ type: 'SET_SELECTED_APPOINTMENTS', appointmentIds });

  useEffect(() => {
    if (skipInitialLoad) return;

    dispatch({ type: 'SET_LOADING' });

    fetchAppointments({ filteredUserId })
      .then((payload) => {
        dispatch({ type: 'SET_DATA', payload });
      })
      .catch((_) => {
        dispatch({ type: 'ERROR' });
      });
  }, [state.currentPage]);

  // Delayed load of total appointments count
  useEffect(() => {
    if (skipInitialLoad) return;

    const timeout = setTimeout(() => {
      fetchAppointmentsCount({ filteredUserId }).then(
        ({ appointmentsCount: count }) => {
          dispatch({ type: 'SET_APPOINTMENTS_COUNT', count });
        },
      );
    }, 750);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <React.Fragment>
      <div className="case-manager">
        {hasCaseReportTypeTabs && (
          <div>
            <Tabs
              defaultType={initialState.caseReportType}
              setType={(caseReportType: string) => {
                dispatch({ type: 'SET_CASE_REPORT_TYPE', caseReportType });
              }}
            />
          </div>
        )}
        <div style={{ marginBottom: 32 }}>
          <Filters {...filterOptions} />
        </div>
        <div style={{ marginBottom: 32 }}>
          <TopBar
            showErrorMessage={state.errored}
            appointmentsCount={skipInitialLoad ? 0 : state.appointmentsCount}
            selectedAppointments={state.selectedAppointments}
            assignableUsers={assignableUsers}
          />
        </div>
        <LoadingOverlay loading={state.loading}>
          {!state.errored && (
            <IndexTable
              appointments={state.appointments}
              assignableUsers={assignableUsers}
              caseReportType={state.caseReportType}
              onSelectedChange={setSelectedAppointments}
            />
          )}
        </LoadingOverlay>
        {skipInitialLoad && (
          <div className="my-4 text-center text-muted fw-bold">
            Please apply filters to see results.
          </div>
        )}
      </div>

      {!skipPagination && state.appointments.length > 0 && (
        <TablePagination
          {...state.meta}
          setPage={(page: number) => dispatch({ type: 'SET_PAGE', page })}
        />
      )}
    </React.Fragment>
  );
};

export default Index;
